import { clockwiseDegreesToSceneRadians, DecorationError, parseLogoRegionTransform, sceneRadiansToClockwiseDegrees, resolveMeasureAnchorOffset, resolvePointOfMeasure, rotateVector, } from '@seddi/style-decoration';
const EPS = 1e-6;
const TECHNIQUE_NAME_BY_VALUE = {
    RUBBER: 'Rubber',
    WATERBASED: 'Waterbased',
    GLITTER: 'Glitter',
    HIGHDENSITY: 'HighDensity',
    PUFF: 'Puff',
    HEATTRANSFER: 'HeatTransfer',
    DIRECTTOGARMENT: 'DirectToGarment',
    EMBROIDERY: 'Embroidery',
    PATCH: 'Patch',
    CUSTOM: 'Custom',
};
function nearlyEqual(a, b) {
    return Math.abs(a - b) <= EPS;
}
function vectorsEqual(a, b) {
    if (!a || !b)
        return a === b;
    return nearlyEqual(a.x, b.x) && nearlyEqual(a.y, b.y);
}
function deepClone(value) {
    return JSON.parse(JSON.stringify(value));
}
function isRecord(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
export function resolveRecipeLogoId(logo, index) {
    if (isRecord(logo) && typeof logo.id === 'string' && logo.id.trim()) {
        return logo.id.trim();
    }
    return String(index + 1);
}
function logoDisplayName(logo) {
    return (logo.displayName || logo.name || '').trim();
}
function toDecorationRegions(styleRegions) {
    return styleRegions.map(region => ({
        id: region.id,
        name: region.name,
        pieceName: region.pieceName,
        side: String(region.side),
        size: { x: region.size.x, y: region.size.y },
        transform: region.transform,
    }));
}
function toDecorationPoms(pointsOfMeasure) {
    return pointsOfMeasure.map(pom => ({
        id: pom.id,
        name: pom.name,
        pieceName: pom.pieceName,
        transform: pom.transform,
    }));
}
function readOriginalPlacementAt(original) {
    if (!isRecord(original.placement) || !isRecord(original.placement.at)) {
        return {};
    }
    const measure = original.placement.at.measure;
    if (!isRecord(measure))
        return {};
    const from = typeof measure.from === 'string' ? measure.from.trim() : undefined;
    const to = typeof measure.to === 'string' ? measure.to : undefined;
    return { from, to };
}
function resolveLogoRegionName(logo, styleRegions) {
    var _a;
    const regionId = (_a = logo.region) === null || _a === void 0 ? void 0 : _a.trim();
    if (!regionId)
        return '';
    const regionById = styleRegions.find(region => region.id === regionId);
    return ((regionById === null || regionById === void 0 ? void 0 : regionById.name) || regionId).trim();
}
function candidatesOnRegionPiece(logo, styleRegions, pointsOfMeasure) {
    const region = styleRegions.find(r => { var _a; return r.id === ((_a = logo.region) === null || _a === void 0 ? void 0 : _a.trim()); });
    if (!region) {
        return pointsOfMeasure;
    }
    const piece = region.pieceName.trim().toLowerCase();
    const onPiece = pointsOfMeasure.filter(pom => pom.pieceName.trim().toLowerCase() === piece);
    return onPiece.length > 0 ? onPiece : pointsOfMeasure;
}
function closestPomToRegion(logo, styleRegions, candidates) {
    if (candidates.length === 0)
        return undefined;
    const region = styleRegions.find(r => { var _a; return r.id === ((_a = logo.region) === null || _a === void 0 ? void 0 : _a.trim()); });
    if (!region)
        return candidates[0];
    const regionCenter = parseLogoRegionTransform(region.transform).translation;
    let best = candidates[0];
    let bestDist = Number.POSITIVE_INFINITY;
    for (const pom of candidates) {
        const pt = parseLogoRegionTransform(pom.transform).translation;
        const dx = pt.x - regionCenter.x;
        const dy = pt.y - regionCenter.y;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
            bestDist = dist;
            best = pom;
        }
    }
    return best;
}
/**
 * Pick a POM on the current logo region's piece.
 * When the region is unchanged, reuse preferredName via style-decoration resolution.
 */
function pickPointOfMeasure(params) {
    const { logo, baseline, styleRegions, pointsOfMeasure, preferredName } = params;
    const candidates = candidatesOnRegionPiece(logo, styleRegions, pointsOfMeasure);
    if (candidates.length === 0)
        return undefined;
    const regionUnchanged = logo.region.trim() === baseline.region.trim();
    if (regionUnchanged && preferredName) {
        try {
            const resolved = resolvePointOfMeasure(preferredName, toDecorationPoms(pointsOfMeasure), {
                logoRegion: logo.region,
                styleRegions: toDecorationRegions(styleRegions),
            });
            const matched = pointsOfMeasure.find(pom => pom.id === resolved.id);
            if (matched && candidates.some(pom => pom.id === matched.id)) {
                return matched;
            }
        }
        catch (error) {
            if (!(error instanceof DecorationError)) {
                throw error;
            }
        }
    }
    return closestPomToRegion(logo, styleRegions, candidates);
}
/**
 * Inverse of style-decoration `resolvePlacementAtAnchorFrame`:
 * logoCenter = pom + rotate(at, pomRot) - rotate(anchorOffset, logoRot)
 */
export function computePlacementAtFromLogoCenter(params) {
    const pomFrame = parseLogoRegionTransform(params.pomTransform);
    const anchorOffset = rotateVector(resolveMeasureAnchorOffset(params.measureTo, params.logoSize.x, params.logoSize.y), params.logoRotation);
    const atWorld = {
        x: params.logoCenter.x - pomFrame.translation.x + anchorOffset.x,
        y: params.logoCenter.y - pomFrame.translation.y + anchorOffset.y,
    };
    return rotateVector(atWorld, -pomFrame.rotation);
}
function toTechniquePayload(technique, customTechnique) {
    const normalized = String(technique || '')
        .trim()
        .toUpperCase()
        .replace(/_/g, '');
    const mapped = TECHNIQUE_NAME_BY_VALUE[normalized];
    if (!mapped)
        return undefined;
    if (mapped === 'Custom') {
        const customValue = customTechnique === null || customTechnique === void 0 ? void 0 : customTechnique.trim();
        return customValue ? { name: mapped, value: customValue } : undefined;
    }
    return { name: mapped };
}
function httpsSourceUrl(value) {
    var _a;
    const trimmed = (_a = value === null || value === void 0 ? void 0 : value.trim()) !== null && _a !== void 0 ? _a : '';
    return trimmed.toLowerCase().startsWith('https://') ? trimmed : undefined;
}
function readRotationDegrees(logo) {
    if (!isRecord(logo.transform))
        return undefined;
    const rotation = logo.transform.rotation;
    if (typeof rotation !== 'number' || !Number.isFinite(rotation))
        return undefined;
    return rotation;
}
function rotationMatchesDegrees(radians, degrees) {
    return nearlyEqual(radians, clockwiseDegreesToSceneRadians(degrees));
}
/** Keep the recipe's degree value when the scene rotation still matches it. */
function serializeRotationDegrees(original, currentRadians) {
    const originalDegrees = readRotationDegrees(original);
    if (originalDegrees !== undefined &&
        rotationMatchesDegrees(currentRadians, originalDegrees)) {
        return originalDegrees;
    }
    return sceneRadiansToClockwiseDegrees(currentRadians);
}
function writeTransformRotation(logo, degrees) {
    const existing = isRecord(logo.transform) ? logo.transform : {};
    logo.transform = Object.assign(Object.assign({}, existing), { rotation: degrees });
}
function hasGeometryChange(current, baseline) {
    return (!vectorsEqual(current.transform.translation, baseline.transform.translation) ||
        !vectorsEqual(current.size, baseline.size) ||
        !nearlyEqual(current.transform.rotation, baseline.transform.rotation) ||
        current.region.trim() !== baseline.region.trim());
}
function hasMetadataChange(current, baseline) {
    return (current.technique !== baseline.technique ||
        (current.customTechnique || '').trim() !== (baseline.customTechnique || '').trim() ||
        logoDisplayName(current) !== logoDisplayName(baseline));
}
function hasSourceUrlChange(current, baseline) {
    var _a;
    const currentUrl = httpsSourceUrl(current.sourceUrl);
    if (!currentUrl)
        return false;
    return currentUrl !== ((_a = httpsSourceUrl(baseline.sourceUrl)) !== null && _a !== void 0 ? _a : '');
}
function isLogoUnchanged(current, baseline) {
    return (!hasGeometryChange(current, baseline) &&
        !hasMetadataChange(current, baseline) &&
        !hasSourceUrlChange(current, baseline));
}
function writePlacementAt(params) {
    var _a;
    const { logo, baseline, styleRegions, pointsOfMeasure, preferredFrom, preferredTo } = params;
    const regionName = resolveLogoRegionName(logo, styleRegions);
    const regionUnchanged = logo.region.trim() === baseline.region.trim();
    const measureTo = regionUnchanged && preferredTo ? preferredTo : 'Center';
    const pom = pickPointOfMeasure({
        logo,
        baseline,
        styleRegions,
        pointsOfMeasure,
        preferredName: preferredFrom,
    });
    const measureFrom = ((_a = pom === null || pom === void 0 ? void 0 : pom.name) === null || _a === void 0 ? void 0 : _a.trim()) || regionName || 'Center';
    if (pom) {
        const at = computePlacementAtFromLogoCenter({
            logoCenter: logo.transform.translation,
            logoSize: logo.size,
            logoRotation: logo.transform.rotation,
            pomTransform: pom.transform,
            measureTo,
        });
        return {
            at: {
                x: at.x,
                y: at.y,
                measure: {
                    from: measureFrom,
                    to: measureTo,
                },
            },
        };
    }
    return {
        at: {
            x: 0,
            y: 0,
            measure: {
                from: measureFrom,
                to: measureTo,
            },
        },
    };
}
function patchChangedLogo(params) {
    const { original, current, baseline, styleRegions, pointsOfMeasure } = params;
    const next = deepClone(original);
    const regionName = resolveLogoRegionName(current, styleRegions);
    if (regionName) {
        next.region = regionName;
    }
    if (!vectorsEqual(current.size, baseline.size)) {
        next.size = { x: current.size.x, y: current.size.y };
    }
    const nextSourceUrl = httpsSourceUrl(current.sourceUrl);
    if (nextSourceUrl && nextSourceUrl !== httpsSourceUrl(baseline.sourceUrl)) {
        next.sourceUrl = nextSourceUrl;
    }
    if (hasMetadataChange(current, baseline)) {
        if (current.technique !== baseline.technique ||
            (current.customTechnique || '').trim() !== (baseline.customTechnique || '').trim()) {
            const technique = toTechniquePayload(current.technique, current.customTechnique);
            if (technique) {
                next.technique = technique;
            }
        }
        if (logoDisplayName(current) !== logoDisplayName(baseline)) {
            const name = logoDisplayName(current);
            if (name) {
                next.name = name;
            }
        }
    }
    if (hasGeometryChange(current, baseline)) {
        next.fill = false;
        const originalAt = readOriginalPlacementAt(original);
        next.placement = writePlacementAt({
            logo: current,
            baseline,
            styleRegions,
            pointsOfMeasure,
            preferredFrom: originalAt.from,
            preferredTo: originalAt.to,
        });
    }
    writeTransformRotation(next, serializeRotationDegrees(original, current.transform.rotation));
    return next;
}
function buildNewRecipeLogo(params) {
    const { logo, styleRegions, pointsOfMeasure } = params;
    const sourceUrl = httpsSourceUrl(logo.sourceUrl);
    if (!sourceUrl)
        return null;
    const regionName = resolveLogoRegionName(logo, styleRegions);
    const technique = toTechniquePayload(logo.technique, logo.customTechnique);
    const name = logoDisplayName(logo) || logo.name || 'Logo';
    const placement = writePlacementAt({
        logo,
        baseline: logo,
        styleRegions,
        pointsOfMeasure,
    });
    const entry = {
        id: logo.id,
        name,
        sourceUrl,
        fill: false,
        size: { x: logo.size.x, y: logo.size.y },
        placement,
        transform: { rotation: sceneRadiansToClockwiseDegrees(logo.transform.rotation) },
    };
    if (regionName) {
        entry.region = regionName;
    }
    if (technique) {
        entry.technique = technique;
    }
    return entry;
}
/**
 * Rebuilds a recipe JSON body from the original file, applying local logo edits.
 * Top-level fields outside `logos` (including ignored ones like requestedImages) are preserved.
 */
export function buildRecipeBody(params) {
    const { recipeSource, currentLogos, baselineLogos, styleRegions, pointsOfMeasure } = params;
    if (!isRecord(recipeSource)) {
        return recipeSource;
    }
    const body = deepClone(recipeSource);
    const originalLogos = Array.isArray(body.logos) ? body.logos : [];
    const currentById = new Map(currentLogos.map(logo => [logo.id, logo]));
    const baselineById = new Map(baselineLogos.map(logo => [logo.id, logo]));
    const nextLogos = [];
    const originalIds = new Set();
    for (let index = 0; index < originalLogos.length; index++) {
        const original = originalLogos[index];
        if (!isRecord(original))
            continue;
        const id = resolveRecipeLogoId(original, index);
        originalIds.add(id);
        const current = currentById.get(id);
        if (!current) {
            // Removed in the UI.
            continue;
        }
        const sourceUrl = typeof original.sourceUrl === 'string' ? original.sourceUrl.trim() : '';
        if (!sourceUrl.toLowerCase().startsWith('https://')) {
            continue;
        }
        const baseline = baselineById.get(id);
        if (baseline && isLogoUnchanged(current, baseline)) {
            nextLogos.push(deepClone(original));
            continue;
        }
        if (!baseline) {
            nextLogos.push(deepClone(original));
            continue;
        }
        nextLogos.push(patchChangedLogo({
            original,
            current,
            baseline,
            styleRegions,
            pointsOfMeasure,
        }));
    }
    for (const logo of currentLogos) {
        if (originalIds.has(logo.id))
            continue;
        const entry = buildNewRecipeLogo({ logo, styleRegions, pointsOfMeasure });
        if (entry) {
            nextLogos.push(entry);
        }
    }
    body.logos = nextLogos;
    return body;
}
